import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import ErrorModal from "../components/ErrorModal";
import { usePermission } from "../hooks/usePermission";

export default function BrandList() {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("brands.create");
  const canEdit = hasPermission("brands.edit");
  const canDelete = hasPermission("brands.delete");

  const [brands, setBrands] = useState([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [deletingBrand, setDeletingBrand] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });

  useEffect(() => {
    fetchBrands();
  }, [search, sortField, sortOrder]);

  const fetchBrands = () => {
    setLoadingBrands(true);
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("sort", sortField);
    params.append("order", sortOrder);

    apiFetch(`/brands?${params.toString()}`)
      .then(setBrands)
      .catch((err) => {
        console.error("Fout bij ophalen merken:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoadingBrands(false));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleAdd = () => {
    setEditingBrand(null);
    setFormData({ name: "", description: "" });
    setShowModal(true);
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({ name: brand.name, description: brand.description || "" });
    setShowModal(true);
  };

  const handleDelete = (brand) => {
    setDeletingBrand(brand);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!deletingBrand) return;

    setLoading(true);
    apiFetch(`/brands/${deletingBrand.id}`, { method: "DELETE" })
      .then(() => {
        setShowDeleteModal(false);
        setDeletingBrand(null);
        fetchBrands();
      })
      .catch((err) => {
        console.error("Fout bij verwijderen brand:", err);
        if (err.message.includes("400")) {
          setErrorModal({ show: true, message: err.message });
        } else {
          setErrorModal({ show: true, message: "Er is een fout opgetreden bij het verwijderen van het merk." });
        }
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const method = editingBrand ? "PATCH" : "POST";
    const url = editingBrand ? `/brands/${editingBrand.id}` : "/brands";

    apiFetch(url, {
      method,
      body: JSON.stringify(formData),
    })
      .then(() => {
        setShowModal(false);
        setFormData({ name: "", description: "" });
        fetchBrands();
      })
      .catch((err) => {
        console.error("Fout bij opslaan brand:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het opslaan van het merk." });
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoading(false));
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "↕";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  if (unauthorized) return <Navigate to="/unauthorized" replace />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Merken</h1>
        {canCreate && (
          <button
            onClick={handleAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            + Nieuw Merk
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-4 flex gap-2">
        <input
          type="text"
          placeholder="Zoek op naam..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-4 py-2 border rounded"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="px-4 py-2 border rounded hover:bg-gray-100 text-gray-700"
          >
            Wissen
          </button>
        )}
      </div>

      {/* Loading Indicator */}
      {loadingBrands && (
        <div className="flex justify-center items-center py-12 bg-white shadow rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Merken laden...</span>
        </div>
      )}

      {/* Brands Table */}
      {!loadingBrands && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort("name")}
                >
                  Naam {getSortIcon("name")}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort("description")}
                >
                  Beschrijving {getSortIcon("description")}
                </th>
                {(canEdit || canDelete) && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Acties
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {brands.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                  Geen merken gevonden
                </td>
              </tr>
            ) : (
              brands.map((brand) => (
                <tr key={brand.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    {brand.name}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {brand.description || "-"}
                  </td>
                  {(canEdit || canDelete) && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {canEdit && (
                        <button
                          onClick={() => handleEdit(brand)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Bewerken
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(brand)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Verwijderen
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingBrand ? "Merk Bewerken" : "Nieuw Merk"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Naam *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beschrijving
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="3"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                  disabled={loading}
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                  disabled={loading}
                >
                  {loading ? "Bezig..." : "Opslaan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingBrand && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Merk Verwijderen</h2>
            <p className="mb-6 text-gray-700">
              Weet je zeker dat je het merk "<strong>{deletingBrand.name}</strong>" wilt verwijderen?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingBrand(null);
                }}
                className="px-4 py-2 border rounded hover:bg-gray-100"
                disabled={loading}
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? "Bezig..." : "Verwijderen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: "" })}
      />
    </div>
  );
}
